import { useEffect, useRef } from 'react';

import { Button } from '@/components/Button';
import { useTranslation } from '@/i18n/useTranslation';

import { SCAN_STATE, useBarcodeScanner } from '../hooks/useBarcodeScanner';
import { isEnterableRead, withoutSeparators } from '../proxyId';

export interface ScanOverlayProps {
  /**
   * The configured prefix the screen holds — `''` while the prefix read is out or did not answer, when the
   * app checks the half it can: 16 digits [03-us-kpc-1e AC-7].
   */
  readonly prefix: string;
  /**
   * The decoded proxy ID, accepted by the candidate.
   *
   * The layer hands the value over and stops there. What happens to it is the screen's — today it fills
   * both proxy-ID fields on the form behind this layer [decision 0142], and nothing is written until the
   * candidate saves.
   */
  readonly onAccept: (proxyId: string) => void;
  /**
   * A barcode decoded, and it is not a proxy id — not 16 digits beginning with the prefix. It is a FAILED
   * READ, reported by the screen and never offered here for confirmation [KPC-1c § *The read is offered for
   * CONFIRMATION*; decisions 0181, 0187; Figma `New user / 10 · Scan — failed read (state)`].
   */
  readonly onFailedRead: () => void;
  /** Close and go to the by-hand step. */
  readonly onTypeInstead: () => void;
  /** Close and hand the card to a person. */
  readonly onAskRecruiter: () => void;
}

/**
 * The camera scan [KPC-1 § *Webcam reads the barcode with focus assist*].
 *
 * **A layer, and it cannot be dismissed by accident.** `DifferentEmailDialog` records why this repository
 * renders prompts as screens: *"a candidate resting a palm on a touchscreen dismisses a modal without
 * meaning to"*. That reasoning is about accidental DISMISSAL, not about layers — so this is a layer with no
 * accidental exit at all. No click-outside, no Escape, no close affordance in a corner. The only ways out
 * are the three deliberate buttons, each of which is a decision the candidate made.
 *
 * The camera is the whole point of the layer: leaving the page visible behind a live video would have the
 * candidate reading their own face over the form they are trying to fill.
 *
 * **Every outcome ends somewhere.** Decoded as a proxy id → confirm, and the screen fills the form. Decoded
 * as anything else → a failed read, reported on the screen behind this layer. Camera refused, camera missing, browser
 * cannot decode, or simply a card that will not read → key it by hand, or a representative keys it. BR-K7
 * makes a dead end at an unattended kiosk a defect, and a scan that can only succeed is a dead end wearing
 * a camera.
 */
export function ScanOverlay({
  prefix,
  onAccept,
  onFailedRead,
  onTypeInstead,
  onAskRecruiter,
}: ScanOverlayProps) {
  const t = useTranslation();
  const { state, value, videoRef, rescan } = useBarcodeScanner(true);
  const panel = useRef<HTMLDivElement | null>(null);
  const heading = useRef<HTMLHeadingElement | null>(null);

  /*
   * ⭐ **Only a read that IS a proxy id is offered** [KPC-1c: *"a read that is not 16 characters beginning
   * `7090` is reported as a failed read rather than offered for confirmation"*; eval-kpc-1c step 4]. Until
   * 2026-10-16 every decoded value reached *Use this card*, and the format was refused only after the
   * candidate had confirmed it — asking someone to vouch for a number the kiosk already knew was wrong.
   */
  const offered = state === SCAN_STATE.found && value !== null ? value : null;
  const refused = offered !== null && !isEnterableRead(withoutSeparators(offered), prefix);

  useEffect(() => {
    if (refused) onFailedRead();
  }, [refused, onFailedRead]);
  /** The read the candidate is asked to confirm — never a refused one, not even for the render before it closes. */
  const confirmable = refused ? null : offered;

  // The layer takes focus when it opens, so a keyboard or a screen reader lands inside it rather than
  // continuing down the form underneath.
  useEffect(() => {
    heading.current?.focus();
  }, []);

  /*
   * A focus loop, not a focus trap library. Tab from the last control returns to the first and Shift+Tab
   * from the first goes to the last — three buttons is a small enough surface to do this honestly. Escape
   * is deliberately NOT bound: see the note on this component.
   */
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Tab' || panel.current === null) return;
      const focusable = panel.current.querySelectorAll<HTMLElement>('button');
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (first === undefined || last === undefined) return;
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
    };
  }, []);

  const message = {
    [SCAN_STATE.starting]: t('payCard.scan.starting'),
    [SCAN_STATE.scanning]: t('payCard.scan.instruction'),
    [SCAN_STATE.found]: t('payCard.scan.found'),
    [SCAN_STATE.denied]: t('payCard.scan.denied'),
    [SCAN_STATE.unsupported]: t('payCard.scan.unsupported'),
    [SCAN_STATE.unavailable]: t('payCard.scan.unavailable'),
  }[state];

  const cameraIsLive = state === SCAN_STATE.starting || state === SCAN_STATE.scanning;

  return (
    <div
      aria-labelledby="scan-title"
      aria-modal="true"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4"
      role="dialog"
    >
      <div
        className="flex w-full max-w-[553px] flex-col gap-5 rounded-[var(--radius-lg)] bg-[var(--card)] p-6"
        ref={panel}
      >
        <div className="flex w-full flex-col gap-2">
          <h2
            className="kiosk-heading-focus w-full text-2xl font-bold leading-7 tracking-[-0.36px] text-[var(--foreground)]"
            id="scan-title"
            ref={heading}
            tabIndex={-1}
          >
            {t('payCard.scan.title')}
          </h2>
          {/*
            `aria-live` so the state changes reach a screen reader: the whole screen is a camera picture,
            and "the camera was refused" is invisible to anyone not looking at it.
          */}
          {/* A refused read says nothing here: "Card read. Check the number" would offer it in words. */}
          <p aria-live="polite" className="w-full text-sm leading-5 text-[var(--muted-foreground)]">
            {refused ? null : message}
          </p>
        </div>

        {cameraIsLive ? (
          <div className="relative w-full overflow-hidden rounded-[var(--radius-md)] bg-black">
            {/*
              Muted and playsInline are not preferences: without them iOS refuses to play inline and opens
              the video full screen, which takes the candidate out of the kiosk entirely. `scale-x-[-1]`
              mirrors the front camera so moving the card left moves it left on screen.
            */}
            <video
              aria-label={t('payCard.scan.videoLabel')}
              autoPlay
              className="aspect-video w-full scale-x-[-1] object-cover"
              muted
              playsInline
              ref={videoRef}
            />
            {/* The frame the candidate lines the barcode up with. Decorative — the instruction is text. */}
            <div
              aria-hidden="true"
              className="pointer-events-none absolute inset-x-[12%] inset-y-[30%] rounded-[var(--radius-sm)] border-2 border-dashed border-white/80"
            />
          </div>
        ) : null}

        {confirmable !== null ? (
          <div className="flex w-full flex-col gap-2 rounded-[var(--radius-md)] bg-[var(--muted)] p-4">
            <p className="text-sm leading-5 text-[var(--muted-foreground)]">
              {t('payCard.idLabel')}
            </p>
            {/*
              The decoded value is shown before it is sent. A scan is not double-keyed — KPC-1 asks for that
              only when the value is TYPED — so this is the one moment a candidate can catch a misread, and
              a wrong proxy ID is discovered on payday otherwise.
            */}
            <p className="font-mono text-xl font-bold leading-7 tracking-[1px] text-[var(--foreground)]">
              {confirmable}
            </p>
          </div>
        ) : null}

        <div className="flex w-full flex-col gap-3">
          {confirmable !== null ? (
            <>
              <Button
                fullWidth
                onClick={() => {
                  onAccept(confirmable);
                }}
              >
                {t('payCard.scan.use')}
              </Button>
              <Button fullWidth onClick={rescan} variant="secondary">
                {t('payCard.scan.again')}
              </Button>
            </>
          ) : null}

          <Button fullWidth onClick={onTypeInstead} variant="secondary">
            {t('payCard.scan.typeInstead')}
          </Button>
          <Button fullWidth onClick={onAskRecruiter} variant="secondary">
            {t('payCard.scan.recruiter')}
          </Button>
        </div>
      </div>
    </div>
  );
}
